import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import { PermissionGuard } from '@app/guards/permission.guard';
import { RoutesWithOptions } from '@app/interfaces';
import { ReportDataResolver } from '@reporting/report-data.resolver';
import { ReportingResolver } from '@reporting/reporting.resolver';
import {
    AssetReportView,
    BulkImportTimeOff,
    CompensationReportView,
    CustomReportDataView,
    EmployeeDocumentsReportView,
    PayrollOverviewView,
    ReportingRoutingView,
    TimeTrackingPayrollReportView,
    VacationYTDBalanceReportView,
    WorkersCompensationReportView,
} from '@reporting/views';
import { BenefitsDigestView } from '@reporting/views/benefits-digest/benefits-digest.view';
import { BulkExportEntityView } from '@reporting/views/bulk-export/bulk-export-entity/bulk-export-entity.view';
import { BulkExportSelectionView } from '@reporting/views/bulk-export/bulk-export-selection/bulk-export-selection.view';
import { BulkImportEntityView } from '@reporting/views/bulk-import/bulk-import-entity/bulk-import-entity.view';
import { BulkImportResultsView } from '@reporting/views/bulk-import/bulk-import-results/bulk-import-results.view';
import { BulkImportSelectionView } from '@reporting/views/bulk-import/bulk-import-selection/bulk-import-selection.view';
import { CustomReportsView } from '@reporting/views/custom-reports/custom-reports.view';
import { CustomReportStoreView } from '@reporting/views/custom-reports/store/custom-report-store.view';
/* Reports */
import { FeatureFlag } from '@app/enums';
import { FeatureGuard } from '@app/guards';
import { MissingDataReportView } from '@reporting/views/insight-reports/missing-data/missing-data.view';
import { InsightsView } from '@reporting/views/insights/insights.view';
import { OverviewView } from '@reporting/views/overview/overview.view';
import { PayrollDigestView } from '@reporting/views/payroll-digest/payroll-digest.view';
import { StandardReportView } from '@reporting/views/standard-report/standard-report.view';
import { TimeOffRequestsReportView } from '@reporting/views/time-off-requests-report/time-off-requests-report.view';
import { TrainingReportView } from '@reporting/views/training-report/training-report.view';
import { JournalEntryReportSettingsView } from './views/journal-entry-report-settings/journal-entry-report-settings.view';
import { JournalEntryReportView } from './views/journal-entry-report/journal-entry-report.view';
import { EnhancedPayrollReportView } from './views/payroll-reports/enhanced-payroll-report/enhanced-payroll-report.view';
import { StandardPayrollReportView } from './views/payroll-reports/standard-payroll-report/standard-payroll-report.view';
import { TimeOffV3Report } from './views/time-off-v3-report/time-off-v3-report.view';
import { TimeToFillReportView } from './views/time-to-fill/time-to-fill-report.view';

const routes: RoutesWithOptions = [
    {
        path: '',
        component: ReportingRoutingView,
        children: [
            { path: '', data: { title: 'reporting.general.title' }, component: OverviewView },
            {
                path: 'payroll',
                data: { title: 'reporting.payroll.title' },
                component: PayrollOverviewView,
            },
            {
                path: 'import',
                data: { title: 'reporting.data-import.title' },
                component: BulkImportSelectionView,
            },
            {
                path: 'export',
                data: { title: 'reporting.data-export.title', permission: ['export.employee', 'create.file'] },
                component: BulkExportSelectionView,
                canActivate: [PermissionGuard],
            },
        ],
    },
    {
        path: 'employee-time-off-balances',
        component: TimeOffV3Report,
    },
    {
        path: 'time-off-requests',
        component: TimeOffRequestsReportView,
    },
    {
        path: 'time-tracking-payroll-report',
        component: TimeTrackingPayrollReportView,
    },
    { path: 'benefits-digest', component: BenefitsDigestView },
    {
        path: 'compensation-report',
        component: CompensationReportView,
        canActivate: [FeatureGuard],
        data: { feature: { flags: [FeatureFlag.compensationReport] } },
    },
    { path: 'payroll-digest', component: PayrollDigestView },

    {
        path: 'training-report',
        component: TrainingReportView,
        resolve: {
            report: ReportDataResolver,
        },
        data: {
            report: 'training',
        },
    },
    {
        path: 'asset-report',
        component: AssetReportView,
        resolve: {
            report: ReportDataResolver,
        },
        data: {
            report: 'asset',
        },
    },
    {
        /**
         * This report is not listed on the report index, can only be access via url.
         * This report is an internal helper, rather than a feature.
         */
        path: 'employee-documents-report',
        component: EmployeeDocumentsReportView,
        resolve: {
            report: ReportDataResolver,
        },
        data: {
            report: 'employee-documents',
        },
    },
    { path: 'insights', component: InsightsView },
    { path: 'insights/missing-data', component: MissingDataReportView },
    { path: 'time-to-fill-report', component: TimeToFillReportView },
    // Payroll Reports
    {
        path: 'payroll',
        children: [
            { path: 'journal-entry', component: JournalEntryReportView },
            { path: 'journal-entry/settings', component: JournalEntryReportSettingsView },
            { path: ':report', component: StandardPayrollReportView },
            { path: 'enhanced/workers-compensation', component: WorkersCompensationReportView },
            { path: 'enhanced/:report', component: EnhancedPayrollReportView },
            { path: 'enhanced/:report/:jurisdiction', component: EnhancedPayrollReportView },
            { path: 'v2/vacation-pay-balances', component: VacationYTDBalanceReportView },
        ],
    },
    // Custom Reports
    {
        path: 'custom-reports',
        component: CustomReportsView,
    },
    {
        path: 'custom-reports',
        children: [
            {
                path: 'new',
                component: CustomReportStoreView,
            },
            {
                path: ':customReportQuery',
                component: CustomReportStoreView,
                resolve: {
                    models: ReportingResolver,
                },
            },
            {
                path: ':id/report',
                component: CustomReportDataView,
                resolve: {
                    report: ReportDataResolver,
                },
                data: {
                    report: 'custom',
                },
            },
        ],
    },
    // Import Reports
    {
        path: 'import',
        children: [
            { path: ':entity', component: BulkImportEntityView },
            {
                path: 'time-off-importers/:entity',
                component: BulkImportTimeOff,
            },
            { path: ':entity/results/:file_id', component: BulkImportResultsView },
        ],
    },
    // Export Reports
    {
        path: 'export',
        canActivate: [PermissionGuard],
        data: { permission: ['export.employee', 'create.file'] },
        children: [
            {
                path: ':entity',
                component: BulkExportEntityView,
            },
        ],
    },
    { path: ':report', component: StandardReportView },
];

@NgModule({
    imports: [RouterModule.forChild(routes)],
    exports: [RouterModule],
})
export class PerformanceRouterModule {}
